/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNumber from 'lodash/isNumber';

// TODO Implementovat skutečnou podporu pro dlouhé časovače.
// - Zatím se provádí primitivní omezení na maximální podporovanou hodnotu,
//   což je ale stále o mnoho lepší, než okamžité vyvolání.
//   Zatím se nepoužívá z žádného kriticky důležitého místa,
//   takže toto zpracování postačuje.
// - Propracované řešení by rozdělovalo každý problematický časovač na několik
//   kratších intervalů, které nepřesáhnou podporované maximum.
//   Přitom bude nutné udržovat vlastní databázi identifikátorů časovačů tak,
//   aby byl uživatel odstíněný od rozdělení na více časovačů.
//   Uživatel vždy pracuje s jedním identifikátorem,
//   kterému v jeden okamžik odpovídá právě jeden "skutečný" identifikátor.
// - https://github.com/nodejs/node-v0.x-archive/issues/8656#issuecomment-67355269
// - https://stackoverflow.com/a/18182660/8315965
// TODO Ošetřit nepřesnost časovačů u dlouhých časovačů.
// - Hodně propracované řešení by ošetřovalo i možnou nepřesnost časovačů,
//   která se bude zhoršovat při rozdělení na více intervalů.
//   Při tomto rozdělení se např. poslední interval zmenší o nabrané zpoždění
//   nebo zrychlení.
// - Pro časovače kratší než maximum pravděpodobně nemá smysl, ty už přesnější
//   nebudou, nepřesnost vzniká až s opakovaným nastavováním časůvačů.
// - https://thecodersblog.com/increase-javascript-timoeout-accuracy/
// - https://medium.com/@abhi9bakshi/why-javascript-timer-is-unreliable-and-how-can-you-fix-it-9ff5e6d34ee0
// - https://github.com/dbkaplun/driftless
// - https://stackoverflow.com/questions/21097421/what-is-the-reason-javascript-settimeout-is-so-inaccurate
// - https://stackoverflow.com/questions/985670/will-setinterval-drift
/**
 * Dlouhé JS časovače, které nemají omezení na maximální dobu.
 * Ačkoliv to specifikace nezmiňuje, většina prohlížečů nedovoluje nastavit
 * časovač na dobu delší, než 25 dní.
 * Navíc v tom případě dojde k okamžitému vyvolání časovače,
 * jako by se jednalo o neplatné číslo.
 *
 * Implementace 100% dodržuje standardizované API časovačů,
 * pro co nejsnažší migraci existujícího kódu a pro snadnou pochopitelnost.
 *
 * Dokumentace:
 * - [MDN](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#Maximum_delay_value)
 * - [StackOverflow](https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values)
 * @implements {Timers}
 */
class LongTimers {
  /**
   * @param {number} maxDelay Maximální doba, na kterou prohlížeč podporuje nastavit
   * časovače, bez jejich okamžitého vyvolání.
   */
  constructor(maxDelay) {
    this.maxDelay = maxDelay;
  }

  /** @inheritdoc */
  clearInterval(id) {
    return clearInterval(id);
  }

  /** @inheritdoc */
  clearTimeout(id) {
    return clearTimeout(id);
  }

  /**
   * Omezí zpoždění časovače na bezpečnou hodnotu.
   *
   * Není úplně jasné, jak má fungovat pro obě `Infinity`.
   * Specifikace žádné speciální ošetření nezmiňuje.
   * Jsou zmiňována omezení jako "less than 0", nebo "greater than 5".
   * Hodnota `Infinity` je kompatibilní s lorovnáním,
   * takže omezení na maximum podléhá.
   *
   * Používá se přístup úpravy seznamu argumentů, pro usnadnění práce
   * s `undefined` hodnotami.
   * @param {Array.<*>} args Pole argumentů ze `setInterval` nebo `setTimeout`.
   * @private
   * @returns {Array.<*>}
   */
  mapSetArgs(args) {
    const delayIndex = 1;
    const delay = args[delayIndex];
    if (!isNumber(delay) || isNaN(delay)) {
      return args;
    }
    return [
      ...args.slice(0, delayIndex),
      Math.min(delay, this.maxDelay),
      ...args.slice(delayIndex + 1)
    ];
  }

  /** @inheritdoc */
  setInterval(...args) {
    const mappedArgs = this.mapSetArgs(args);
    return setInterval(...mappedArgs);
  }

  /** @inheritdoc */
  setTimeout(...args) {
    const mappedArgs = this.mapSetArgs(args);
    return setTimeout(...mappedArgs);
  }
}

export default LongTimers;
